import type { StreakDto } from '../api/journal';
import { formatEta } from '../lib/formatEta';

/**
 * C1/C3: a streak's numeric length floors at 1 for display purposes only. The server's own
 * `StreakDto.length` is 0 for a brand-new account (no `account_streaks` row yet) and, per
 * `JournalService.rollStreak`'s own C1 guard, an unbridgeable gap only ever resets `length` to 1
 * — never to 0, never with a token deducted. Either way the player should read this as a fresh
 * start, never a demoralizing "Day 0": SCREENS.md's own copy rule is "a lapsed streak simply shows
 * 'Day 1'" (zero guilt copy, no reference to what broke, no count of missed days).
 */
export function streakDay(length: number): number {
  return Math.max(length, 1);
}

export type StreakRowProps = {
  streak: StreakDto;
  dailyResetAt: string;
  now: Date;
};

/**
 * SCREENS.md "/journal" ready "Today" section's own streak row: "Day N", the account's saved rest
 * tokens framed as a gift ("2 rest days saved for you" — NEVER "you have 2 grace days left" or any
 * phrasing that implies a debt/penalty, C1/C3), and a coarse reset clock via `formatEta` (never
 * seconds-precision urgency in a retention surface, C3).
 */
export function StreakRow({ streak, dailyResetAt, now }: StreakRowProps) {
  const day = streakDay(streak.length);
  const restLabel = `${streak.restTokens} rest day${streak.restTokens === 1 ? '' : 's'} saved for you`;

  return (
    <div className="journal-streak-row">
      <span className="journal-streak-day">{`Day ${day}`}</span>
      <span className="journal-streak-rest">{restLabel}</span>
      <span className="journal-streak-reset">{`Resets in ${formatEta(dailyResetAt, now)}`}</span>
    </div>
  );
}
